import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useState, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import {
  Wrapper,
  ProgressBar,
  ProgressFill,
  ProgressWrap,
  OtpFlowTitle,
  ProgressLabel,
  OtpFlowSubText
} from "../../common-styles";
import {
  selectLoading,
  setDigitalThankyouConfig
} from "../../store/slices/otpSlice";
import { OTP_LOADING_KEYS } from "../../templates/strings";
import { fetchWorkspaceForDigitalThankyou } from "../../services/api";
import { submitAvailabilityThunk } from "../../store/thunks/otpThunks";

// Availability Options enum
const OPTIONS = [
  { label: "Vormittag", value: "Vormittag" },
  { label: "Mittag", value: "Mittag" },
  { label: "Nachmittag", value: "Nachmittag" },
  { label: "Abend", value: "Abend" }
];

const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ebf2dc;
  border-radius: 6px;
  padding: 8px 14px;
  color: #265b19;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 18px;
  @media (max-width: 481px) {
    font-size: 12px;
    gap: 5px;
  }
`;
const CheckIcon = styled(FontAwesomeIcon)`
  color: #265b19;
`;
const SectionLabel = styled.p`
  font-size: 14px;
  margin-bottom: 10px;
`;
const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 18px;
`;
const OptionBtn = styled.button`
  padding: 12px;
  border: 2px solid ${({ $selected }) => ($selected ? "#c8a96e" : "#ccc")};
  border-radius: 8px;
  background: ${({ $selected }) => ($selected ? "#f5f0ea" : "#fff")};
  font-size: 18px;
  font-weight: ${({ $selected }) => ($selected ? 700 : 400)};
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    background: #fafafa;
    border-color: #aaa;
  }
  @media (max-width: 481px) {
    padding: 8px;
    font-size: 14px;
  }
`;
const SubmitBtn = styled.button`
  width: 100%;
  padding: 16px 0;
  background: #e15d3a;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  cursor: ${({ $active }) => ($active ? "pointer" : "not-allowed")};
  opacity: ${({ $active }) => ($active ? 1 : 0.6)};
  transition: opacity 0.2s ease;
  @media (max-width: 481px) {
    padding: 12px 0;
    font-size: 16px;
  }
`;

const AvailabilityPage = ({ leadId }) => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(null);
  const isSubmitting = useSelector(
    selectLoading(OTP_LOADING_KEYS.SUBMIT_AVAILABILITY)
  );

  const handleSelect = (option) => {
    if (!isSubmitting) {
      setSelected(option.value);
    }
  };
  const handleSubmit = () => {
    if (!selected || isSubmitting) return;
    dispatch(
      submitAvailabilityThunk({ leadId, optimalAvailability: selected })
    );
  };

  const getDigitalThankyou = async (_leadId) => {
    try {
      const response = await fetchWorkspaceForDigitalThankyou();
      dispatch(
        setDigitalThankyouConfig(response.data.data.digitalThankyouPage)
      );
    } catch (error) {
      console.log("Error in getting digital thankyou: ", error);
    }
  };
  const getDigitalThankyouCb = useCallback(getDigitalThankyou, [dispatch]);
  useEffect(() => {
    getDigitalThankyouCb();
  }, [getDigitalThankyouCb]);

  return (
    <Wrapper>
      <ProgressWrap>
        <ProgressBar>
          <ProgressFill $width="100" />
        </ProgressBar>
        <ProgressLabel>Letzter Schritt</ProgressLabel>
      </ProgressWrap>

      <SuccessBanner>
        <CheckIcon size="lg" icon={faCheckCircle} />
        <span>Nummer bestätigt</span>
      </SuccessBanner>

      <OtpFlowTitle>Wann passt es dir am besten?</OtpFlowTitle>
      <OtpFlowSubText>Damit wir dich sofort richtig erreichen.</OtpFlowSubText>

      <SectionLabel>
        <strong>Beste Erreichbarkeit:</strong>
      </SectionLabel>

      {/* 2×2 option grid*/}
      <OptionsGrid>
        {OPTIONS.map((opt) => (
          <OptionBtn
            key={opt.value}
            $selected={selected === opt.value}
            onClick={() => handleSelect(opt)}
          >
            {opt.label}
          </OptionBtn>
        ))}
      </OptionsGrid>

      {/* Orange submit — disabled until selection made */}
      <SubmitBtn
        $active={!!selected}
        disabled={!selected || isSubmitting}
        onClick={handleSubmit}
      >
        {isSubmitting ? "Wird gesendet..." : "Anfrage absenden"}
      </SubmitBtn>
    </Wrapper>
  );
};

export default AvailabilityPage;
